import { RefObject, useRef } from 'react';
import clsx from 'clsx';
import { MessageBox, MessageBoxType } from '@/components/layout/MessageBox';
import { Loader } from '@/components/generic/Loader';
import { sanitizeTypeformString } from '@/services/SurveyService';
import { useProjectContext } from '@/providers/ProjectContextProvider';
import { useCurrentUser } from '@/providers/CurrentUserProvider';
import {
  convertArrayToObject,
  getExportFileName,
} from '@/services/FileService';
import { ExportToImageModal } from '@/components/generic/export/ExportableToImage';
import { useModal } from '@/components/modals/Modal';
import { utils, writeFileXLSX } from 'xlsx';
import {
  ImpactLevel,
  MaterialityTypes,
  MatrixArray_AnswerFragment,
  MatrixArray_StakeFragment,
  PriorityLevel,
} from '@/graphql/generated';
import { getImpactLabel, getPriorityLabel } from '@/services/DiagnosticService';
import { DownloadIcon, ExportIcon } from '@/components/icons';
import { useTranslation } from '@/hooks/useTranslation';
import { ContextualMenu } from '@/components/generic/ContextualMenu';

export type MatrixRowType = {
  stakeId: string;
  label: string;
  values: MatrixCellType[];
};

export type MatrixCellType = {
  key: string;
  label: string;
  value: number;
};

export function MatrixArray({
  title,
  matrix,
  columns,
}: {
  title?: string | null | undefined;
  matrix: MatrixRowType[];
  columns: string[];
}) {
  const projectContext = useProjectContext();
  const { currentUser } = useCurrentUser();
  const exportableRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  if (!matrix.length) {
    return (
      <>
        {title ? (
          <div className="p-4 border border-gray-100 rounded-2xl shadow-sm bg-white relative">
            {title && (
              <div className="font-bold mb-4 mr-16">
                {sanitizeTypeformString(title, projectContext, currentUser)}
              </div>
            )}
            <MessageBox type={MessageBoxType.Info}>
              {t('survey.results.matrix.no_data')}
            </MessageBox>
          </div>
        ) : (
          <MessageBox type={MessageBoxType.Info}>
            {t('survey.results.matrix.no_data')}
          </MessageBox>
        )}
      </>
    );
  }

  return (
    <div className="p-4 border border-gray-100 rounded-2xl shadow-sm bg-white relative">
      <div className="absolute top-4 right-4 z-40">
        <ExportMatrixMenu matrix={matrix} exportableRef={exportableRef} />
      </div>
      <div ref={exportableRef}>
        {title && (
          <div className="font-bold mb-4 mr-16">
            {sanitizeTypeformString(title, projectContext, currentUser)}
          </div>
        )}
        <MatrixRows matrix={matrix} columns={columns} />
      </div>
    </div>
  );
}

function MatrixRows({
  matrix,
  columns,
}: {
  matrix: MatrixRowType[];
  columns: string[];
}) {
  const { t } = useTranslation();

  if (matrix.length > 0) {
    const numberOfColumns = columns.length;
    return (
      <div className="flex flex-col items-stretch gap-4 justify-evenly">
        <MatrixHeader matrix={matrix} numberOfColumns={numberOfColumns} />
        {matrix.map((row) => (
          <MatrixRow
            key={row.stakeId}
            row={row}
            numberOfColumns={numberOfColumns}
          />
        ))}
      </div>
    );
  } else {
    return (
      <MessageBox type={MessageBoxType.Info}>
        {t('survey.results.matrix.no_data')}
      </MessageBox>
    );
  }
}

function MatrixHeader({
  matrix,
  numberOfColumns,
}: {
  matrix: MatrixRowType[];
  numberOfColumns: number;
}) {
  // Fetch header labels from first row
  const headerLabels =
    matrix.length > 0 ? matrix[0].values.map((value) => value.label) : [];
  return (
    <div className="flex items-center justify-between w-full">
      <div className="sami-body-bold w-1/3"></div>
      <div className={clsx('w-2/3 grid gap-3', `grid-cols-${numberOfColumns}`)}>
        {headerLabels.map((label) => (
          <div key={label} className="text-gray-500 text-center sami-body-small">
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}

function MatrixRow({
  row,
  numberOfColumns,
}: {
  row: MatrixRowType;
  numberOfColumns: number;
}) {
  const sumOfAllValues = row.values.reduce(
    (sum, value) => sum + value.value,
    0,
  );
  return (
    <div className="flex items-center justify-between w-full">
      <div className="w-1/3 sami-body-small">{row.label || <Loader />}</div>
      <div className={clsx('w-2/3 grid gap-3', `grid-cols-${numberOfColumns}`)}>
        {row.values.map((cell) => (
          <MatrixCell
            key={cell.key}
            cell={cell}
            sumOfAllValues={sumOfAllValues}
          />
        ))}
      </div>
    </div>
  );
}

function MatrixCell({
  cell,
  sumOfAllValues,
}: {
  cell: MatrixCellType;
  sumOfAllValues: number;
}) {
  const percentage = sumOfAllValues > 0 ? cell.value / sumOfAllValues : 0;
  const textColor = percentage < 0.5 ? 'text-purple-500' : 'text-white';
  return (
    <div
      className={clsx(
        'flex items-center justify-center gap-1 rounded-lg p-3 sami-body-bold',
        textColor,
      )}
      style={{ backgroundColor: `rgba(94, 67, 149, ${percentage})` }}
    >
      <div className="">{Math.round(percentage * 100)}%</div>
      <div className={clsx('sami-body sami-body-small', textColor)}>{cell.value}</div>
    </div>
  );
}

function ExportMatrixToImageButton({
  refToExport,
}: {
  refToExport: RefObject<HTMLDivElement | null>;
}) {
  const { t } = useTranslation();
  const modal = useModal();
  return (
    <button
      className="contextual-menu-item"
      onClick={() =>
        modal.openModal(
          <ExportToImageModal
            refToExport={refToExport}
            name={getExportFileName(t('survey.results.matrix.export.filename'))}
          />,
          {
            title: t('survey.results.matrix.export.title'),
            widthClass: 'w-8/12',
          },
        )
      }
    >
      <DownloadIcon />
      <div>Image</div>
    </button>
  );
}

function ExportMatrixContentToXlsButton({
  matrix,
}: {
  matrix: MatrixRowType[];
}) {
  const { t } = useTranslation();
  const exportToXls = () => {
    // Fetch header labels from first row
    const headerLabels =
      matrix.length > 0 ? matrix[0].values.map((value) => value.label) : [];

    // For each column, add % column
    const header = [
      t('survey.results.matrix.export.header'),
      ...headerLabels,
      ...headerLabels.map((c) => `${c} (%)`),
    ];

    const rows = matrix.map((row) => {
      const sumOfAllValues = row.values.reduce(
        (sum, value) => sum + value.value,
        0,
      );
      const rowValues = row.values.map((cell) => {
        return cell.value;
      });
      // Add percentage columns
      const rowPercentageValues = row.values.map((cell) => {
        return sumOfAllValues > 0
          ? Math.round((100 * cell.value) / sumOfAllValues)
          : 0;
      });
      return [`${row.label}`, ...rowValues, ...rowPercentageValues];
    });

    // Convert array to object to be able to use xlsx library
    const asAnObject = convertArrayToObject(rows, header);

    const worksheet = utils.json_to_sheet(asAnObject, {
      skipHeader: false,
    });

    const workbook = utils.book_new();
    utils.book_append_sheet(
      workbook,
      worksheet,
      t('survey.results.matrix.export.sheet'),
    );
    writeFileXLSX(
      workbook,
      `${t('survey.results.matrix.export.filename')}.xlsx`,
      {
        compression: true,
      },
    );
  };

  return (
    <button className="contextual-menu-item" onClick={exportToXls}>
      <DownloadIcon />
      <div>{t('survey.results.matrix.export.xls_button')}</div>
    </button>
  );
}

function ExportMatrixMenu({
  matrix,
  exportableRef,
}: {
  matrix: MatrixRowType[];
  exportableRef: RefObject<HTMLDivElement | null>;
}) {
  const button = <ExportIcon className="shrink-0 h-3.5 w-3.5 text-gray-500" />;
  return (
    <ContextualMenu button={button}>
      <ExportMatrixContentToXlsButton matrix={matrix} />
      <ExportMatrixToImageButton refToExport={exportableRef} />
    </ContextualMenu>
  );
}

export function stakeAnswersToMatrixRows(
  answers: MatrixArray_AnswerFragment[],
  stakes: MatrixArray_StakeFragment[],
  columns: Array<PriorityLevel | ImpactLevel>,
  materialityType: MaterialityTypes,
): MatrixRowType[] {
  const { t, translateProperty } = useTranslation();
  return (
    answers
      .flatMap((answer) => answer.stakes)
      .reduce((acc: MatrixRowType[], stakeAnswer) => {
        if (acc.find((row) => row.stakeId === stakeAnswer?.stakeId)) {
          // Increment value in the right cell
          const stakeId = stakeAnswer?.stakeId || '';
          const level =
            materialityType === MaterialityTypes.Financial
              ? stakeAnswer?.priority
              : stakeAnswer?.impact;
          const row = acc.find((row) => row.stakeId === stakeId);
          const cell = row?.values.find((cell) => cell.key === level);
          if (cell) {
            cell.value++;
          }
          return acc;
        } else {
          const stake = stakes.find(
            (stake) => stake.id === stakeAnswer?.stakeId,
          );

          return [
            ...acc,
            {
              stakeId: stakeAnswer?.stakeId || '',
              label: translateProperty(stake, 'name'),
              values: columns.map((column) => {
                const label =
                  materialityType === MaterialityTypes.Financial
                    ? getPriorityLabel(t, column as PriorityLevel)
                    : getImpactLabel(t, column as ImpactLevel);
                const value =
                  materialityType === MaterialityTypes.Financial
                    ? stakeAnswer?.priority === column
                      ? 1
                      : 0
                    : stakeAnswer?.impact === column
                      ? 1
                      : 0;
                return {
                  key: column,
                  label,
                  value,
                };
              }),
            },
          ];
        }
      }, [])
      .toSorted((a, b) => a.label.localeCompare(b.label)) || []
  );
}
